import type { CSSProperties } from 'react';

const FONT_SCALE_KEY = 'courtyard_word_font_scale';

export const FONT_SCALE_MIN = 0.7;
export const FONT_SCALE_MAX = 1.3;
export const FONT_SCALE_STEP = 0.05;
export const FONT_SCALE_DEFAULT = 1;

const LEGACY_FONT_SCALES = [0.85, 1, 1.15, 1.3];

function clampScale(scale: number): number {
  const stepped =
    FONT_SCALE_MIN +
    Math.round((scale - FONT_SCALE_MIN) / FONT_SCALE_STEP) * FONT_SCALE_STEP;
  return Math.min(FONT_SCALE_MAX, Math.max(FONT_SCALE_MIN, stepped));
}

function migrateLegacyIndex(raw: string): number | null {
  const idx = parseInt(raw, 10);
  if (!Number.isInteger(idx) || idx < 0 || idx >= LEGACY_FONT_SCALES.length) return null;
  if (raw !== String(idx)) return null;
  return clampScale(LEGACY_FONT_SCALES[idx]);
}

export function getFontScale(): number {
  if (typeof window === 'undefined') return FONT_SCALE_DEFAULT;

  const raw = localStorage.getItem(FONT_SCALE_KEY);
  if (!raw) return FONT_SCALE_DEFAULT;

  const legacy = migrateLegacyIndex(raw);
  if (legacy != null) {
    setFontScale(legacy);
    return legacy;
  }

  const parsed = parseFloat(raw);
  if (!Number.isFinite(parsed)) return FONT_SCALE_DEFAULT;

  const scale = parsed > 2 ? parsed / 100 : parsed;
  return clampScale(scale);
}

export function setFontScale(scale: number): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(FONT_SCALE_KEY, String(clampScale(scale)));
}

export function decreaseFontScale(): number {
  const next = clampScale(getFontScale() - FONT_SCALE_STEP);
  setFontScale(next);
  return next;
}

export function increaseFontScale(): number {
  const next = clampScale(getFontScale() + FONT_SCALE_STEP);
  setFontScale(next);
  return next;
}

export function canDecreaseFontScale(): boolean {
  return getFontScale() > FONT_SCALE_MIN + 1e-9;
}

export function canIncreaseFontScale(): boolean {
  return getFontScale() < FONT_SCALE_MAX - 1e-9;
}

export function getFontScaleLabel(scale: number = getFontScale()): string {
  const percent = Math.round(scale * 100);
  if (percent === 100) return '1:1';
  return `${percent}%`;
}

/** Scales the entire add-in UI (text, icons, spacing) — not just inherited font size. */
export function getUiScaleStyle(scale: number = getFontScale()): CSSProperties {
  return { zoom: scale };
}

const DEBUG_LOG_PANEL_KEY = 'courtyard_word_show_debug_log_panel';
let debugLogPanelListeners: Array<() => void> = [];

/** Whether the cross-check debug log panel (copy/clear) is visible. Default: hidden. */
export function isDebugLogPanelVisible(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(DEBUG_LOG_PANEL_KEY) === '1';
  } catch {
    return false;
  }
}

export function setDebugLogPanelVisible(next: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    if (next) {
      localStorage.setItem(DEBUG_LOG_PANEL_KEY, '1');
    } else {
      localStorage.removeItem(DEBUG_LOG_PANEL_KEY);
    }
  } catch {
    // ignore
  }
  debugLogPanelListeners.forEach((cb) => {
    try {
      cb();
    } catch {
      // ignore
    }
  });
}

export function subscribeDebugLogPanelVisible(onChange: () => void): () => void {
  debugLogPanelListeners.push(onChange);
  return () => {
    debugLogPanelListeners = debugLogPanelListeners.filter((cb) => cb !== onChange);
  };
}
